// ABOUTME: React hook for managing admin user impersonation state and session
// ABOUTME: Fetches impersonation status from API - backend is source of truth for security

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../services/api';
import { IMPERSONATION_CONSTANTS } from '../constants/impersonation';

export interface ImpersonatedUser {
  userId: number;
  email: string;
  fullName: string;
  firstName: string;
  lastName: string;
  profileImageUrl: string | null;
}

export interface UseImpersonationReturn {
  isImpersonating: boolean;
  impersonatedUser: ImpersonatedUser | null;
  isLoading: boolean;
  startImpersonation: (userId: number) => Promise<void>;
  endImpersonation: () => Promise<void>;
  refreshStatus: () => Promise<void>;
  getImpersonationHeader: () => Record<string, string>;
}

export function useImpersonation(): UseImpersonationReturn {
  const { user } = useAuth();
  // Initialize optimistically from sessionStorage before API call
  const [impersonatedUser, setImpersonatedUser] = useState<ImpersonatedUser | null>(() => {
    try {
      const stored = sessionStorage.getItem('impersonatedUserId');
      if (stored) {
        // We have a user ID, but don't have full details yet - API call will fill them in
        // Return a placeholder that will be replaced by the API response
        return { userId: parseInt(stored, 10), email: '', fullName: '', firstName: '', lastName: '', profileImageUrl: null };
      }
    } catch (error) {
      console.error('Failed to read impersonation state on init:', error);
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);

  // Initialize from api module on mount (api.ts loads from sessionStorage)
  useEffect(() => {
    const initialize = async () => {
      if (!user) {
        setImpersonatedUser(null);
        setIsLoading(false);
        return;
      }

      // Verify and get full details from API
      try {
        const status = await api.getImpersonationStatus();
        if (status.isImpersonating && status.impersonatedUser) {
          setImpersonatedUser({
            userId: status.impersonatedUser.userId,
            email: status.impersonatedUser.email,
            fullName: status.impersonatedUser.fullName,
            firstName: status.impersonatedUser.firstName,
            lastName: status.impersonatedUser.lastName,
            profileImageUrl: status.impersonatedUser.profileImageUrl,
          });
        } else {
          setImpersonatedUser(null);
        }
      } catch (error) {
        console.error('Failed to get impersonation status:', error);
        setImpersonatedUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [user]);

  // Refresh status from backend
  const refreshStatus = useCallback(async () => {
    if (!user) {
      setImpersonatedUser(null);
      api.setImpersonatedUserId(null);
      return;
    }

    try {
      const status = await api.getImpersonationStatus();
      if (status.isImpersonating && status.impersonatedUser) {
        setImpersonatedUser({
          userId: status.impersonatedUser.userId,
          email: status.impersonatedUser.email,
          fullName: status.impersonatedUser.fullName,
          firstName: status.impersonatedUser.firstName,
          lastName: status.impersonatedUser.lastName,
          profileImageUrl: status.impersonatedUser.profileImageUrl,
        });
      } else {
        setImpersonatedUser(null);
        api.setImpersonatedUserId(null);
      }
    } catch (error) {
      console.error('Failed to get impersonation status:', error);
      setImpersonatedUser(null);
      api.setImpersonatedUserId(null);
    }
  }, [user]);

  const startImpersonation = useCallback(async (userId: number) => {
    if (!user?.isAdmin) {
      throw new Error(IMPERSONATION_CONSTANTS.ERROR_ADMIN_ONLY);
    }

    try {
      // Call backend to validate and create audit log
      const response = await api.startImpersonation(userId);

      // CRITICAL: Set the user ID BEFORE refreshStatus so the header is sent
      // This allows the middleware to transform claims before /status endpoint reads them
      api.setImpersonatedUserId(userId);

      // Update local state with response data
      setImpersonatedUser({
        userId: response.userId,
        email: response.email,
        fullName: response.fullName,
        firstName: response.firstName || '',
        lastName: response.lastName || '',
        profileImageUrl: response.profileImageUrl || null,
      });
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      // Clear state on error
      api.setImpersonatedUserId(null);
      setImpersonatedUser(null);
      throw error;
    }
  }, [user]);

  const endImpersonation = useCallback(async () => {
    if (!impersonatedUser) {
      return;
    }

    try {
      await api.endImpersonation(impersonatedUser.userId);

      // Clear state immediately - don't wait for refreshStatus()
      // because refreshStatus() would still send the header and see isImpersonating=true
      api.setImpersonatedUserId(null);
      setImpersonatedUser(null);
    } catch (error) {
      console.error('Failed to end impersonation:', error);
      throw error;
    }
  }, [impersonatedUser]);

  const getImpersonationHeader = useCallback((): Record<string, string> => {
    if (!impersonatedUser) {
      return {};
    }

    return {
      [IMPERSONATION_CONSTANTS.HEADER_NAME]: impersonatedUser.userId.toString(),
    };
  }, [impersonatedUser]);

  return {
    isImpersonating: !!impersonatedUser,
    impersonatedUser,
    isLoading,
    startImpersonation,
    endImpersonation,
    refreshStatus,
    getImpersonationHeader,
  };
}
